// components/crud/ClienteList.tsx
import React, { useState, useEffect } from 'react';
import { clienteService } from '../../services/entitiesServices';
import type { Cliente } from '../../types/entities';
import type { PaginatedResponse, QueryParams } from '../../config/endpoints';
import { formatCpfCnpj } from '../../utils/formatters';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface ClienteListProps {
  onEdit?: (cliente: Cliente) => void;
  onView?: (cliente: Cliente) => void;
  // parent can change this number to trigger a refresh
  refreshSignal?: number;
}

export const ClienteList: React.FC<ClienteListProps> = (props) => {
  const { onEdit, onView, refreshSignal } = props;
  const notifier = useNotifier();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [sortBy, setSortBy] = useState<string>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const loadClientes = async (params?: QueryParams & { sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    try {
      setLoading(true);
      setError('');
      
      let response: any;
      
      if (searchTerm.trim()) {
        response = await clienteService.search(searchTerm.trim(), params);
      } else {
        response = await clienteService.list(params);
      }
      
      // Tratar resposta do backend que pode vir em formatos diferentes
      // VERIFICAR PRIMEIRO SE TEM PAGINAÇÃO
      if (response.pagination && response.data) {
        // Formato com paginação: {"success": true, "data": [...], "pagination": {...}}
        setClientes(response.data);
        setPagination(response.pagination);
      } else if (response.success && Array.isArray(response.data)) {
        // Formato: {"success": true, "data": [...]} SEM paginação
        setClientes(response.data);
        setPagination({
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: response.data.length,
          totalPages: 1,
        });
      } else if (Array.isArray(response)) {
        // Formato direto: [...]
        setClientes(response);
        setPagination({
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: response.length,
          totalPages: 1,
        });
      } else {
        setClientes([]);
        setPagination({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar clientes');
      setClientes([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes({ page: 1, limit: 10, sortBy, sortOrder });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof refreshSignal !== 'undefined') {
      // reload first page when parent signals a refresh
      setPagination(prev => ({ ...prev, page: 1 }));
      loadClientes({ page: 1, limit: pagination.limit, sortBy, sortOrder });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadClientes({ page: 1, limit: pagination.limit, sortBy, sortOrder });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (pagination.totalPages && newPage > pagination.totalPages) return;
    setPagination(prev => ({ ...prev, page: newPage }));
    loadClientes({ page: newPage, limit: pagination.limit, sortBy, sortOrder });
  };

  const handleSort = (field: string) => {
    if (field === sortBy) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      setPagination(prev => ({ ...prev, page: 1 }));
      loadClientes({ page: 1, limit: pagination.limit, sortBy: field, sortOrder: newOrder });
    } else {
      setSortBy(field);
      setSortOrder('asc');
      setPagination(prev => ({ ...prev, page: 1 }));
      loadClientes({ page: 1, limit: pagination.limit, sortBy: field, sortOrder: 'asc' });
    }
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) {
      return (
        <svg className="inline-block w-3 h-3 ml-1 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 9l6-6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6 15l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (sortOrder === 'asc') {
      return (
        <svg className="inline-block w-3 h-3 ml-1 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 15l6-6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    return (
      <svg className="inline-block w-3 h-3 ml-1 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const handleDelete = (cliente: Cliente) => {
    setClienteToDelete(cliente);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!clienteToDelete) return;

    try {
      await clienteService.delete(clienteToDelete.id);
      notifier.show({ message: 'Cliente excluído com sucesso!', severity: 'success' });
      loadClientes({ page: pagination.page, limit: pagination.limit });
      setShowDeleteConfirm(false);
      setClienteToDelete(null);
    } catch (err) {
      notifier.show({ 
        message: err instanceof Error ? err.message : 'Erro ao deletar cliente', 
        severity: 'error' 
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Header e Busca */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
          Clientes ({pagination?.total || 0})
        </h2>
        
        <div className="flex gap-1 flex-wrap">
          {/* Campo de ordenação */}
          <div className="flex items-center gap-1">
            <label className="text-xs" style={{ color: '#3d2b56' }}>Ordenar por:</label>
            <select
              value={sortBy}
              onChange={(e) => handleSort(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '120px'
              }}
            >
              <option value="nome">Nome</option>
              <option value="email">Email</option>
              <option value="telefone">Telefone</option>
              <option value="cpfCnpj">CPF/CNPJ</option>
            </select>
            <button
              onClick={() => {
                const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                setSortOrder(newOrder);
                setPagination(prev => ({ ...prev, page: 1 }));
                loadClientes({ page: 1, limit: pagination.limit, sortBy, sortOrder: newOrder });
              }}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          <form onSubmit={handleSearch} className="flex gap-1">
            <input
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '160px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-offset-1 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </form>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="p-3 rounded-md bg-red-50">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="flex justify-center py-6">
          <div className="text-xs" style={{ color: '#9E9E9E' }}>
            Carregando clientes...
          </div>
        </div>
      )}

      {/* Tabela de clientes */}
      {!loading && !error && clientes.length > 0 && (
        <div className="space-y-2">
          {clientes.map((cliente) => (
            <div 
              key={cliente.id}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-3"
              style={{ borderLeft: '4px solid #7A61B2' }}
            >
              {/* Header with Actions */}
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">{cliente.nome}</h3>
                  <p className="text-xs text-gray-500">{formatCpfCnpj(cliente.cpfCnpj)}</p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex space-x-1">
                  {onView && (
                    <button 
                      onClick={() => onView(cliente)} 
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50" 
                      title="Ver Detalhes"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  )}
                  {onEdit && (
                    <button 
                      onClick={() => onEdit(cliente)} 
                      className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                      title="Editar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                  <button 
                    onClick={() => handleDelete(cliente)} 
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50" 
                    title="Excluir"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Email</div>
                    <div className="text-sm text-gray-700 leading-tight truncate">{cliente.email || '-'}</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Telefone</div>
                    <div className="text-sm text-gray-700 leading-tight">{cliente.telefone || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mensagem quando não há dados */}
      {!loading && !error && clientes.length === 0 && (
        <div className="text-center py-8">
          <p className="text-xs" style={{ color: '#9E9E9E' }}>
            {searchTerm ? 'Nenhum cliente encontrado para a busca.' : 'Nenhum cliente cadastrado.'}
          </p>
        </div>
      )}

      {/* Paginação */}
      {!loading && clientes.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-xs" style={{ color: '#9E9E9E' }}>
            {pagination && pagination.total > 0 ? 
              `Mostrando ${((pagination.page - 1) * pagination.limit) + 1} a ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total} clientes` :
              `Mostrando ${clientes.length} clientes`
            }
          </div>
          
          <div className="flex space-x-1">
            <button
              onClick={() => handlePageChange((pagination?.page || 1) - 1)}
              disabled={(pagination?.page || 1) <= 1}
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Anterior
            </button>
            
            <span className="px-2 py-1 text-xs" style={{ color: '#3d2b56' }}>
              Página {pagination?.page || 1}{pagination?.totalPages ? ` de ${pagination.totalPages}` : ''}
            </span>
            
            <button
              onClick={() => handlePageChange((pagination?.page || 1) + 1)}
              disabled={
                pagination?.totalPages 
                  ? (pagination.page >= pagination.totalPages) 
                  : (clientes.length < (pagination?.limit || 10))
              }
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir Cliente"
        message="Tem certeza que deseja excluir este cliente?"
        details={clienteToDelete ? [
          `Nome: ${clienteToDelete.nome}`,
          `CPF/CNPJ: ${clienteToDelete.cpfCnpj || '-'}`,
          `Email: ${clienteToDelete.email || '-'}`
        ] : []}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setClienteToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="#DC2626"
      />
    </div>
  );
};